"use client";

import { useEffect, useState } from "react";
import { EllipsisVertical, LogOut, Moon, Sun } from "lucide-react";
import { toast } from "sonner";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { getInitials } from "@/lib/utils";
import { authService } from "@/services/auth.service";
import { useAuth } from "@/hooks/use-auth";
import { updateThemeMode } from "@/lib/theme-utils";
import { setCookie } from "@/lib/cookies";
import { usePreferencesStore } from "@/stores/preferences/preferences-provider";

export function NavUser({
  user: defaultUser,
}: {
  readonly user: {
    readonly name: string;
    readonly email: string;
    readonly avatar: string;
  };
}) {
  const { isMobile } = useSidebar();
  const { logout } = useAuth();
  const themeMode = usePreferencesStore((s) => s.themeMode);
  const setThemeMode = usePreferencesStore((s) => s.setThemeMode);
  const [user, setUser] = useState<{
    name: string;
    email: string;
    avatar: string;
  }>(defaultUser);

  // Fetch real user data from API
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        // Get Google profile picture from localStorage
        const googlePicture = localStorage.getItem('user_profile_picture');
        
        const profile = await authService.getProfile();
        setUser({
          name: `${profile.firstName} ${profile.lastName}`,
          email: profile.email,
          avatar: googlePicture || defaultUser.avatar, // Use Google picture or fall back to default
        });
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
        // Keep using default user if fetch fails
      }
    };

    fetchUserProfile();
  }, [defaultUser.avatar]);

  const handleThemeToggle = () => {
    const newTheme = themeMode === "dark" ? "light" : "dark";
    updateThemeMode(newTheme);
    setThemeMode(newTheme);
    setCookie("theme_mode", newTheme);
  };

  const handleLogout = () => {
    toast.success("Logged out successfully");
    
    // Use the auth hook's logout which clears tokens, cookies, localStorage, and React Query cache
    logout();
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.avatar || undefined} alt={user.name} />
                <AvatarFallback className="rounded-lg">{getInitials(user.name)}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{user.name}</span>
                <span className="text-muted-foreground truncate text-xs">{user.email}</span>
              </div>
              <EllipsisVertical className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user.avatar || undefined} alt={user.name} />
                  <AvatarFallback className="rounded-lg">{getInitials(user.name)}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user.name}</span>
                  <span className="text-muted-foreground truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleThemeToggle}>
              {themeMode === 'dark' ? (
                <>
                  <Sun />
                  Light mode
                </>
              ) : (
                <>
                  <Moon />
                  Dark mode
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
